import React, { useState } from 'react';
import { Department } from '../types';
import { db, handleFirestoreError, OperationType } from '../lib/firebase';
import { collection, addDoc, updateDoc, doc, deleteDoc } from 'firebase/firestore';
import { Plus, Edit2, Trash2, Save } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import LCARSConfirmationModal from './LCARSConfirmationModal';

interface DepartmentManagerProps {
  departments: Department[];
}

export default function DepartmentManager({ departments }: DepartmentManagerProps) {
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingDept, setEditingDept] = useState<Department | null>(null);
  const [formData, setFormData] = useState<Partial<Department>>({
    name: ''
  });

  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    title: string;
    description: string;
    onConfirm: () => void;
    variant: 'danger' | 'info';
  }>({
    isOpen: false,
    title: '',
    description: '',
    onConfirm: () => {},
    variant: 'danger'
  });

  const askConfirmation = (title: string, description: string, onConfirm: () => void, variant: 'danger' | 'info' = 'danger') => {
    setConfirmDialog({
      isOpen: true,
      title,
      description,
      onConfirm,
      variant
    });
  };

  const handleEdit = (dept: Department) => {
    setEditingDept(dept);
    setFormData(dept);
    setIsFormOpen(true);
  };

  const handleDelete = async (id: string) => {
    askConfirmation(
      'Confirm Department Deletion',
      'Are you sure you want to delete this department? Existing members assigned to this department will remain assigned to the text value, but it will no longer be an option for new members.',
      async () => {
        try {
          await deleteDoc(doc(db, 'departments', id));
          toast.success('Department removed from records');
        } catch (error) {
          handleFirestoreError(error, OperationType.DELETE, 'departments');
        }
      }
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (!formData.name) {
        toast.error('Missing required fields');
        return;
      }

      const targetName = formData.name.trim();

      // Duplicate prevention checks (case-insensitive, trimmed)
      if (!editingDept) {
        const isDuplicate = departments.some(d => d.name.toLowerCase().trim() === targetName.toLowerCase());
        if (isDuplicate) {
          toast.error(`Department "${targetName}" already exists.`);
          return;
        }
      } else {
        const isDuplicate = departments.some(d => d.id !== editingDept.id && d.name.toLowerCase().trim() === targetName.toLowerCase());
        if (isDuplicate) {
          toast.error(`Department "${targetName}" already exists under a different ID.`);
          return;
        }
      }

      const dbData = { name: targetName };
      
      if (editingDept) {
        await updateDoc(doc(db, 'departments', editingDept.id), dbData);
        toast.success('Department updated');
      } else {
        await addDoc(collection(db, 'departments'), dbData);
        toast.success('New department commissioned');
      }
      setIsFormOpen(false);
    } catch (error) {
      handleFirestoreError(error, OperationType.WRITE, 'departments');
    }
  };

  return (
    <div className="bg-[var(--background)] border-l-8 border-lcars-blue pl-4 lg:pl-6 py-2">
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 mb-6">
        <div className="flex items-center gap-4">
          <span className="text-xl font-bold text-lcars-blue uppercase">Department Management</span>
        </div>
        <button 
          onClick={() => { setEditingDept(null); setFormData({ name: '' }); setIsFormOpen(true); }}
          className="lcars-pill bg-lcars-blue text-black px-6 py-1.5 text-sm font-bold flex items-center gap-2"
        >
          <Plus className="w-4 h-4" /> ADD NEW DEPARTMENT
        </button>
      </div>

      <div className="overflow-auto border-t border-lcars-blue/20">
        <table className="w-full border-collapse">
          <thead>
            <tr className="text-lcars-blue text-[0.7rem] font-bold tracking-widest uppercase">
              <th className="text-left p-4 border-b-2 border-lcars-blue/30">ID</th>
              <th className="text-left p-4 border-b-2 border-lcars-blue/30">NAME</th>
              <th className="text-right p-4 border-b-2 border-lcars-blue/30">ACTIONS</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-lcars-blue/10">
            {departments.map(dept => (
              <tr key={dept.id} className="hover:bg-lcars-blue/5 transition-colors group">
                <td className="p-4 font-mono text-lcars-blue/60">{dept.id}</td>
                <td className="p-4">
                  <div className="font-bold text-lg text-[var(--foreground)]">{dept.name}</div>
                </td>
                <td className="p-4 text-right">
                  <div className="flex justify-end gap-2">
                    <button 
                      onClick={() => handleEdit(dept)}
                      className="p-2 bg-lcars-purple/20 text-lcars-purple rounded-full hover:bg-lcars-purple hover:text-black transition-colors"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button 
                      onClick={() => handleDelete(dept.id)}
                      className="p-2 bg-lcars-red/20 text-lcars-red rounded-full hover:bg-lcars-red hover:text-[var(--foreground)] transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={isFormOpen} onOpenChange={setIsFormOpen}>
        <DialogContent className="bg-card-bg border border-border-color text-[var(--foreground)] max-w-md font-lcars p-0 overflow-hidden">
          <DialogHeader className="p-6 bg-white/5 border-b border-border-color">
            <DialogTitle className="uppercase text-lcars-blue tracking-widest font-bold">
              {editingDept ? 'Modify Department' : 'New Department Specification'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="p-8 space-y-6">
            <div className="space-y-2">
              <Label className="text-[0.7rem] uppercase font-bold text-[var(--foreground)]/40 tracking-widest">Department Name</Label>
              <Input 
                value={formData.name || ''} 
                onChange={e => setFormData({ ...formData, name: e.target.value })}
                className="bg-[var(--background)] border-border-color focus:border-lcars-blue"
                required
              />
            </div>

            <DialogFooter className="pt-6 border-t border-white/10 flex gap-4">
              <button 
                type="button" 
                onClick={() => setIsFormOpen(false)}
                className="lcars-pill bg-lcars-tan text-black px-6 py-2 text-sm font-bold"
              >
                DISCARD
              </button>
              <button 
                type="submit" 
                className="lcars-pill bg-lcars-blue text-black px-10 py-2 text-sm font-bold flex items-center gap-2"
              >
                <Save className="w-4 h-4" /> SAVE RECORD
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <LCARSConfirmationModal
        isOpen={confirmDialog.isOpen}
        onClose={() => setConfirmDialog(prev => ({ ...prev, isOpen: false }))}
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        description={confirmDialog.description}
        variant={confirmDialog.variant}
      />
    </div>
  );
}
